import React, { useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { apiPost, apiPatch } from '../utils/api';
import { Moon, Sun, ArrowLeft, Mail, Shield, Calendar, LogOut, Camera, Upload, Lock, Eye, EyeOff, ChevronRight, User as UserIcon, Plug, HelpCircle, Crown, ShieldCheck, Pencil, Check, X as XIcon } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { ResponsiveLogo } from '../components/ThemeLogo';
import MobileBottomNav from '../components/MobileBottomNav';
import NotificationBell from '../components/NotificationBell';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE_URL}${url}`;
};

export default function ProfilePage() {
  const { user, logout, checkAuth } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);

  const [showCropDialog, setShowCropDialog] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 80, aspect: 1 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Change password state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // OTP flow state for Google-auth users
  const [otpStep, setOtpStep] = useState('send'); // send | verify | set
  const [otpCode, setOtpCode] = useState('');
  const [setToken, setSetToken] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [changingPassword, setChangingPassword] = useState(false);

  // Edit name state
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);

  const handleEditName = () => {
    setNameValue(user?.name || '');
    setEditingName(true);
  };

  const handleSaveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed) { toast.error('Name cannot be empty'); return; }
    if (trimmed === user?.name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      const res = await apiPatch('/auth/me', { name: trimmed });
      if (res.ok) {
        toast.success('Name updated');
        setEditingName(false);
        await checkAuth();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update name');
      }
    } catch { toast.error('Failed to update name'); }
    finally { setSavingName(false); }
  };

  const handleCancelName = () => { setEditingName(false); };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <span className="px-3 py-1 rounded-full bg-odapto-orange/10 text-odapto-orange text-sm font-medium">Admin</span>;
      case 'privileged':
        return <span className="px-3 py-1 rounded-full bg-odapto-teal/10 text-odapto-teal text-sm font-medium">Privileged</span>;
      default:
        return <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">Normal</span>;
    }
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image must be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
        setShowCropDialog(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const size = Math.min(width, height, 300);
    setCrop({
      unit: 'px',
      width: size,
      height: size,
      x: (width - size) / 2,
      y: (height - size) / 2,
    });
  }, []);

  const getCroppedImg = async () => {
    if (!imgRef.current || !completedCrop) return null;

    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
    });
  };

  const handleUpload = async () => {
    setUploading(true);
    try {
      const blob = await getCroppedImg();
      if (!blob) {
        toast.error('Please select a crop area');
        return;
      }

      const formData = new FormData();
      formData.append('file', blob, 'profile.jpg');

      const response = await apiPost('/auth/profile-photo', formData, true);
      if (response.ok) {
        toast.success('Profile photo updated!');
        setShowCropDialog(false);
        setImageSrc(null);
        await checkAuth(); // Refresh user data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await apiPost('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      if (response.ok) {
        toast.success('Password changed successfully!');
        resetPasswordDialog();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const resetPasswordDialog = () => {
    setShowPasswordDialog(false);
    setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    setOtpStep('send'); setOtpCode(''); setSetToken('');
  };

  const handleSendOtp = async () => {
    setOtpSending(true);
    try {
      const res = await apiPost('/auth/send-password-otp', {});
      if (res.ok) { toast.success('Verification code sent to your email'); setOtpStep('verify'); setOtpCooldown(60); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed to send code'); }
    } catch { toast.error('Failed to send code'); }
    setOtpSending(false);
  };

  const handleVerifyOtp = async () => {
    setChangingPassword(true);
    try {
      const res = await apiPost('/auth/verify-password-otp', { otp: otpCode });
      if (res.ok) { const d = await res.json(); setSetToken(d.set_token); setOtpStep('set'); toast.success('Code verified'); }
      else { const e = await res.json(); toast.error(e.detail || 'Invalid code'); }
    } catch { toast.error('Verification failed'); }
    setChangingPassword(false);
  };

  const handleSetPassword = async () => {
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setChangingPassword(true);
    try {
      const res = await apiPost('/auth/set-password', { set_token: setToken, new_password: newPassword, confirm_password: confirmPassword });
      if (res.ok) { toast.success('Password set successfully!'); resetPasswordDialog(); await checkAuth(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed to set password'); }
    } catch { toast.error('Failed to set password'); }
    setChangingPassword(false);
  };

  // OTP cooldown timer
  React.useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  // ── Mobile profile menu items ──
  const menuItems = [
    { icon: UserIcon, label: 'Profile', desc: 'View and edit your profile details', action: () => toast.info('You are viewing your profile'), color: 'text-odapto-orange', bg: 'bg-odapto-orange/10' },
    { icon: Plug, label: 'Integrations', desc: 'Manage your connected apps', action: () => navigate('/integrations'), color: 'text-odapto-orange', bg: 'bg-odapto-orange/10' },
    { icon: Lock, label: 'Change Password', desc: 'Update your account password', action: () => setShowPasswordDialog(true), color: 'text-odapto-orange', bg: 'bg-odapto-orange/10' },
    { icon: HelpCircle, label: 'Help', desc: 'Get help and view support resources', action: () => toast.info('Help center coming soon'), color: 'text-odapto-orange', bg: 'bg-odapto-orange/10' },
    { icon: Crown, label: 'Upgrade Plan', desc: 'Unlock premium features', action: () => navigate('/upgrade'), color: 'text-odapto-orange', bg: 'bg-odapto-orange/10', highlight: true },
    { icon: LogOut, label: 'Log out', desc: 'Sign out from your account', action: handleLogout, color: 'text-red-500', bg: 'bg-red-500/10', danger: true },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      {/* ═══ Top Navigation ═══ */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              {/* Desktop: back arrow | Mobile: logo only */}
              <Link to="/dashboard" className="hidden md:block p-2 hover:bg-muted rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <ResponsiveLogo className="h-8 w-auto" />
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <div className="md:hidden"><NotificationBell /></div>
              <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-muted transition-colors" data-testid="theme-toggle">
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ═══ MOBILE PROFILE LAYOUT ═══ */}
      <div className="md:hidden px-4 pt-6 pb-28" data-testid="mobile-profile">
        {/* Heading */}
        <h1 className="text-2xl font-bold mb-1">Profile</h1>
        <p className="text-sm text-muted-foreground mb-6">Manage your account and preferences</p>

        {/* User Card */}
        <div className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card mb-6">
          <div className="relative group">
            <Avatar className="h-12 w-12">
              <AvatarImage src={getImageUrl(user?.picture)} alt={user?.name} />
              <AvatarFallback className="bg-odapto-orange text-white text-lg">{getInitials(user?.name)}</AvatarFallback>
            </Avatar>
            <button onClick={() => fileInputRef.current?.click()} className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-4 h-4 text-white" />
            </button>
          </div>
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-1.5">
                <Input
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') handleCancelName(); }}
                  className="h-8 text-sm font-bold"
                  autoFocus
                  data-testid="mobile-edit-name-input"
                />
                <Button size="icon" variant="ghost" onClick={handleSaveName} disabled={savingName} className="h-7 w-7 text-green-600 shrink-0" data-testid="mobile-save-name-btn">
                  <Check className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" onClick={handleCancelName} className="h-7 w-7 text-muted-foreground shrink-0" data-testid="mobile-cancel-name-btn">
                  <XIcon className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <p className="font-bold truncate" onClick={handleEditName}>{user?.name}</p>
            )}
            <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
        </div>

        {/* Menu Items */}
        <div className="space-y-2 mb-8">
          {menuItems.map((item, i) => {
            const Icon = item.icon;
            return (
              <button
                key={i}
                onClick={item.action}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-border bg-card active:bg-muted/50 transition-colors text-left"
                data-testid={`mobile-menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-5 h-5 ${item.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-sm ${item.danger ? 'text-red-500' : item.highlight ? 'text-odapto-orange' : ''}`}>{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            );
          })}
        </div>

        {/* Security Footer */}
        <div className="text-center text-xs text-muted-foreground pb-4">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Your data is secure with us.</span>
          </div>
          <p>
            Read our <Link to="/privacy" className="text-odapto-orange hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-odapto-orange hover:underline">Terms of Service</Link>.
          </p>
        </div>
      </div>

      {/* ═══ DESKTOP PROFILE LAYOUT (unchanged) ═══ */}
      <main className="hidden md:block max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="bg-gradient-to-br from-odapto-orange/20 to-odapto-teal/20 p-8">
            <div className="flex items-center gap-6">
              <div className="relative group">
                <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                  <AvatarImage src={getImageUrl(user?.picture)} alt={user?.name} />
                  <AvatarFallback className="bg-odapto-orange text-white text-2xl">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  data-testid="change-photo-btn"
                >
                  <Camera className="w-6 h-6 text-white" />
                </button>
              </div>
              <div>
                {editingName ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={nameValue}
                      onChange={(e) => setNameValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') handleCancelName(); }}
                      className="h-9 text-lg font-bold bg-white/80 dark:bg-zinc-800/80"
                      autoFocus
                      data-testid="edit-name-input"
                    />
                    <Button size="icon" variant="ghost" onClick={handleSaveName} disabled={savingName} className="h-8 w-8 text-green-600 hover:bg-green-50" data-testid="save-name-btn">
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={handleCancelName} className="h-8 w-8 text-muted-foreground hover:bg-muted" data-testid="cancel-name-btn">
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 group/name">
                    <h1 className="font-heading text-2xl font-bold mb-2">{user?.name}</h1>
                    <button onClick={handleEditName} className="p-1 rounded hover:bg-black/10 transition-colors opacity-0 group-hover/name:opacity-100 mb-2" data-testid="edit-name-btn" title="Edit name">
                      <Pencil className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                )}
                {getRoleBadge(user?.role)}
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6">
            <div className="grid gap-4">
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <div className="w-10 h-10 rounded-full bg-odapto-orange/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-odapto-orange" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <div className="w-10 h-10 rounded-full bg-odapto-teal/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-odapto-teal" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Account Type</p>
                  <p className="font-medium capitalize">{user?.role}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">User ID</p>
                  <p className="font-medium font-mono text-sm">{user?.user_id}</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <h3 className="font-semibold mb-3">Your Permissions</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500" /> Create workspaces and boards</li>
                <li className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500" /> Use templates from the gallery</li>
                {(user?.role === 'privileged' || user?.role === 'admin') && (
                  <li className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-odapto-teal" /> Publish boards as templates</li>
                )}
                {user?.role === 'admin' && (
                  <>
                    <li className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-odapto-orange" /> Manage users and roles</li>
                    <li className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-odapto-orange" /> Manage template categories</li>
                  </>
                )}
              </ul>
            </div>

            <div className="pt-4 border-t border-border space-y-3">
              <Button variant="outline" onClick={() => setShowPasswordDialog(true)} className="w-full" data-testid="change-password-btn">
                <Lock className="w-4 h-4 mr-2" /> Change Password
              </Button>
              <Button variant="outline" onClick={handleLogout} className="w-full text-destructive hover:bg-destructive/10" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Log out
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Hidden file input for photo upload */}
      <input ref={fileInputRef} type="file" accept="image/*" onChange={onSelectFile} className="hidden" data-testid="photo-input" />

      {/* Crop Dialog */}
      <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Crop Profile Photo</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {imageSrc && (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img src={imageSrc} onLoad={onImageLoad} alt="Crop preview" style={{ maxHeight: '400px' }} />
              </ReactCrop>
            )}
            <p className="text-xs text-muted-foreground mt-2">Drag to adjust the crop area. Max 2MB.</p>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowCropDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading}
              className="bg-odapto-orange hover:bg-odapto-orange-hover text-white"
              data-testid="upload-photo-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(o) => { if (!o) resetPasswordDialog(); else setShowPasswordDialog(true); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{user?.has_password ? 'Change Password' : 'Set Password'}</DialogTitle>
            <DialogDescription>
              {user?.has_password
                ? 'Enter your current password and choose a new one.'
                : otpStep === 'send' ? 'Verify your identity via email to set a password.'
                : otpStep === 'verify' ? 'Enter the code sent to your email.'
                : 'Choose your new password.'}
            </DialogDescription>
          </DialogHeader>

          {/* ── Normal password user: current + new + confirm ── */}
          {user?.has_password ? (
            <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <div className="relative">
                  <Input id="current-password" type={showCurrentPw ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required data-testid="current-password-input" />
                  <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input id="new-password" type={showNewPw ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} data-testid="new-password-input" />
                  <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input id="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="confirm-password-input" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={resetPasswordDialog}>Cancel</Button>
                <Button type="submit" disabled={changingPassword} className="bg-odapto-orange hover:bg-odapto-orange-hover text-white" data-testid="change-password-submit-btn">
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          ) : (
            /* ── Google-auth user: OTP flow ── */
            <div className="space-y-4 mt-4">
              {otpStep === 'send' && (
                <>
                  <div className="space-y-2">
                    <Label>Registered Email</Label>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                      <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium">{user?.email}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">A verification code will be sent to this email.</p>
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={resetPasswordDialog}>Cancel</Button>
                    <Button onClick={handleSendOtp} disabled={otpSending} className="bg-odapto-orange hover:bg-odapto-orange-hover text-white" data-testid="send-otp-btn">
                      {otpSending ? 'Sending...' : 'Send Code'}
                    </Button>
                  </div>
                </>
              )}
              {otpStep === 'verify' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="otp-input">Verification Code</Label>
                    <Input id="otp-input" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit code" maxLength={6} className="text-center text-lg tracking-widest font-mono" autoFocus data-testid="otp-input" />
                    <p className="text-xs text-muted-foreground">Check your email ({user?.email}) for the code.</p>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <button onClick={handleSendOtp} disabled={otpCooldown > 0 || otpSending} className="text-xs text-odapto-orange hover:underline disabled:text-muted-foreground disabled:no-underline" data-testid="resend-otp-btn">
                      {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : 'Resend Code'}
                    </button>
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={resetPasswordDialog}>Cancel</Button>
                      <Button onClick={handleVerifyOtp} disabled={otpCode.length !== 6 || changingPassword} className="bg-odapto-orange hover:bg-odapto-orange-hover text-white" data-testid="verify-otp-btn">
                        {changingPassword ? 'Verifying...' : 'Verify Code'}
                      </Button>
                    </div>
                  </div>
                </>
              )}
              {otpStep === 'set' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="new-password-otp">New Password</Label>
                    <div className="relative">
                      <Input id="new-password-otp" type={showNewPw ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={8} autoFocus data-testid="new-password-input" />
                      <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password-otp">Confirm New Password</Label>
                    <Input id="confirm-password-otp" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="confirm-password-input" />
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={resetPasswordDialog}>Cancel</Button>
                    <Button onClick={handleSetPassword} disabled={changingPassword} className="bg-odapto-orange hover:bg-odapto-orange-hover text-white" data-testid="set-password-btn">
                      {changingPassword ? 'Setting...' : 'Set Password'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <MobileBottomNav />
    </div>
  );
}
